import axios from 'axios';
import isString from 'lodash-es/isString';
import isUndefined from 'lodash-es/isUndefined';

import { getApiBase, EApiBaseCode, EApiFrontDefined, RESPONSE_ERROR_MESSAGE } from '@/constants';

import { reportCustomEvent } from '../arms';

import type {
    IHandleFetchSuccessProps,
    IHandleFetchSuccessResult,
    IRequestDefineConfig,
    IRequestOptions,
    IResponse,
    TFormatOption,
    TOriginResponse,
    TRequestMethod,
} from './typings';
import type { AxiosError, AxiosRequestConfig } from 'axios';

// 格式化请求配置，给参数直接添加默认值，并对 axios 相关配置做处理
export const formatRequestOptions = (options: IRequestDefineConfig & IRequestOptions) => {
    const {
        // 是否需要原始数据，默认 false
        isNeedOriginRes = false,
        // 是否去除Get请求参数中的空字符串，默认true
        skipEmptyString = true,
        // 是否默认对字符串类型的请求参数做一次trim操作，默认true
        trimStringParams = true,
        // 合法的ApiCodes list，如果业务中认为某些非0的ApiCode也是成功，可以传递这样一个白名单数组，默认空数组
        validApiCodes = [],
        // 当请求非200，是否弹出错误信息，默认true
        showErrorWhenBadResponse = true,
        // 请求非200，弹出的错误信息文案，默认responseErrorMessage.defaultErrorText
        badResponseErrorText = RESPONSE_ERROR_MESSAGE.DEFAULT_ERROR_TEXT,
        // 当请求是200，但是得到的后端自定义code不是0，是否弹出错误信息，默认true
        showErrorWhenBadCode = true,
        // 请求是200但是非0的code，弹出的错误信息文案,默认responseErrorMessage.defaultErrorText
        badCodeResponseErrorText = RESPONSE_ERROR_MESSAGE.DEFAULT_ERROR_TEXT,
        // 请求是200但是非0的code，弹出的错误信息文案,是否使用后端返回的message错误信息字段，默认false
        // 如果该配置是 true，则会覆盖 badCodeResponseErrorText 配置的文案
        showApiResponseErrorMessage = false,
        // axios 配置
        axiosConfig: axiosConfigInit = {},
        // 请求域名
        domain = getApiBase(),
        // 自定义header，会被merge到最终的header中
        header = {},
    } = options;

    const axiosConfig: AxiosRequestConfig = {
        responseType: 'json',
        baseURL: domain,
        withCredentials: true, // 跨域请求时携带 cookie
        ...axiosConfigInit,
    };

    // 自定义header，会被merge到最终的header中
    axiosConfig.headers = {
        ...axiosConfig.headers,
        ...header,
    };

    return {
        isNeedOriginRes,
        skipEmptyString,
        trimStringParams,
        validApiCodes,
        showErrorWhenBadResponse,
        badResponseErrorText,
        showErrorWhenBadCode,
        badCodeResponseErrorText,
        showApiResponseErrorMessage,
        axiosConfig,
        domain,
    };
};

interface IFormatRequestParamsProps<RequestData> {
    options: TFormatOption;
    method: TRequestMethod;
    data?: RequestData;
}

// 在请求发出前，格式化请求参数，包括
// 1. 过滤null值和undefined的值，理论上这部分的工作axios也会帮我们做
// 2. 对字符串类型的请求参数做 trim 操作（由config.skipEmptyString）控制
// 3. 去除Get请求参数中的空字符串，默认true
export const formatRequestParams = <RequestData>(
    props: IFormatRequestParamsProps<RequestData>
): Record<string, unknown> => {
    const { options, method, data } = props;
    if (!data) {
        return {};
    }
    const { skipEmptyString, trimStringParams } = options;
    const formatParams: Record<string, unknown> = {};
    Object.entries(data).forEach(([key, value]) => {
        if (value === null || isUndefined(value)) {
            // 过滤null值和undefined的值，理论上这部分的工作axios也会帮我们做
        } else if (isString(value)) {
            const formatValue = trimStringParams ? value.trim() : value;
            // get请求时默认过滤空字符串，可通过skipEmptyString避免过滤
            if (formatValue !== '' || method.toLocaleLowerCase() !== 'get' || !skipEmptyString) {
                formatParams[key] = formatValue;
            }
        } else {
            formatParams[key] = value;
        }
    });

    // TODO: 为所有url加上全局都要带上的公参
    //     const commonParams = await getGlobalUrlQueryParams(exceptParams, config);

    return formatParams;
};

// 该函数处理 http status 为 200 时的成功逻辑，包括
// 1. 校验反馈结构
// 2. 校验自定义 code 是否正常
export const handleFetchSuccess = <ResponseData>(
    props: IHandleFetchSuccessProps<ResponseData>
): IHandleFetchSuccessResult<ResponseData> => {
    const { originResponse, options, duration } = props;
    const { validApiCodes } = options;
    const { data: responseData } = originResponse;

    // 期望返回json但是接口并没有返回json
    if (
        !String(originResponse.headers['content-type'] || '').includes('application/json') &&
        originResponse.config.responseType === 'json'
    ) {
        originResponse.data = {
            code: EApiFrontDefined.NotJson,
            message: `接口${originResponse.config.url}返回的格式不为json，前端兜底处理`,
        };
        reportCustomEvent('APIRequestFailed', `接口返回的格式不为json`, {
            properties: {
                url: originResponse.config.url,
                duration,
                method: originResponse.config.method,
                status_code: originResponse.status,
                business_code: EApiFrontDefined.NotJson,
                response_data: JSON.stringify(originResponse.data),
                query_params: JSON.stringify(originResponse.config?.params),
                body_params: JSON.stringify(originResponse.config?.data),
            },
        });
        return {
            success: false,
            originResponse,
        };
    }
    // 如果是二进制文件，直接返回
    if (responseData instanceof Blob || originResponse.config.responseType === 'text') {
        return {
            success: true,
            originResponse,
        };
    }

    const { code } = responseData;
    if (code === EApiBaseCode.SUCCESS || validApiCodes.includes(code)) {
        reportCustomEvent('APIRequestSuccess', `接口请求成功`, {
            properties: {
                url: originResponse.config.url,
                duration,
                method: originResponse.config.method,
                status_code: originResponse.status,
                business_code: code,
                response_data: JSON.stringify(originResponse.data),
                query_params: JSON.stringify(originResponse.config?.params),
                body_params: JSON.stringify(originResponse.config?.data),
            },
        });
        return {
            success: true,
            originResponse,
        };
    }

    return {
        success: false,
        originResponse,
    };
};

// 该函数处理异常请求的结果，包括响应不是20x，以及自定义ApiErrorCode不为0,
export const handleFetchFailed = <ResponseData>(
    error: TOriginResponse<ResponseData> | AxiosError<IResponse<ResponseData>>,
    options: TFormatOption,
    duration: number
) => {
    const {
        showErrorWhenBadCode,
        showErrorWhenBadResponse,
        // badResponseErrorText,
        // badCodeResponseErrorText,
        // showApiResponseErrorMessage,
    } = options;

    // 取消axios请求导致的err，直接抛出让业务层处理
    if (axios.isCancel(error)) {
        return Promise.reject(error);
    }

    if (axios.isAxiosError(error)) {
        reportCustomEvent('APIRequestFailed', error.message, {
            properties: {
                url: error.config?.url,
                duration,
                method: error.config?.method,
                status_code: error.response?.status,
                business_code: -1,
                response_data: JSON.stringify(error.response?.data),
                query_params: JSON.stringify(error.config?.params),
                body_params: JSON.stringify(error.config?.data),
            },
        });

        const data: IResponse<ResponseData> | undefined = error.response?.data;
        if (data) {
            const { code } = data;
            if (code === EApiBaseCode.TOKEN_MISSING || code === EApiBaseCode.TOKEN_INVALID) {
                //  TODO: 用户 token 缺失或无效 处理
                // message.error(errorMessage);
            } else if (code === EApiBaseCode.KICKED_OFFLINE) {
                //  TODO: 用户被踢下线 处理
                // message.error(errorMessage);
            } else if (showErrorWhenBadResponse) {
                // TODO: 其他错误
                // message.error(errorMessage);
            }
        }
        return error;
    }

    const { data: responseData } = error;
    const { code } = responseData;

    // 请求2xx & 业务code错误码非登录错误码
    const { message: responseMessage } = responseData;
    const errorMsg = responseMessage ?? RESPONSE_ERROR_MESSAGE.DEFAULT_ERROR_TEXT;
    reportCustomEvent('APIRequestFailed', errorMsg, {
        properties: {
            url: error.config?.url,
            duration,
            method: error.config?.method,
            status_code: 200,
            business_code: code,
            response_data: JSON.stringify(responseData),
            query_params: JSON.stringify(error.config?.params),
            body_params: JSON.stringify(error.config?.data),
        },
    });

    if (showErrorWhenBadCode) {
        // const errorMessage = showApiResponseErrorMessage ? errorMsg : badCodeResponseErrorText;
        // TODO: 错误信息展示
        // message.error(errorMessage);
    }
    return error;
};
